import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import Sidebar from "@/components/sidebar";
import Login from "@/pages/login";
import VerifyOtp from "@/pages/verify-otp";
import SalesDashboard from "@/pages/sales-dashboard";
import Dashboard from "@/pages/dashboard";
import SeriesPage from "@/pages/series";
import Articles from "@/pages/articles";
import CreateArticle from "@/pages/create-article";
import EditVideo from "@/pages/edit-video";
import CategoriesPage from "@/pages/categories";
import AuthorsPage from "@/pages/authors";
import BadgesPage from "@/pages/badges";
import BadgeOrderPage from "@/pages/badge-order";
import MilestonesPage from "@/pages/milestones";
import PromotionsPage from "@/pages/promotions";
import EventsPage from "@/pages/events";
import EventDetailsPage from "@/pages/event-details";
import ProgramsPage from "@/pages/programs";
import AddProgramPage from "@/pages/add-program";
import CustomersPage from "@/pages/customers";
import PartnershipsPage from "@/pages/partnerships";
import TrialsPage from "@/pages/trials";
import NewslettersPage from "@/pages/newsletters";
import AddNewsletterPage from "@/pages/add-newsletter";
import NewsletterTemplatePage from "@/pages/newsletter-template";
import UserManagement from "@/pages/user-management";
import CompanyAuditPage from "@/pages/company-audit";
import Settings from "@/pages/settings";
import ImportPage from "@/pages/import";
import PulseSurveyCategoriesPage from "@/pages/pulse-survey-categories";
import PulseSurveyQuestionsPage from "@/pages/pulse-survey-questions";
import PulseSurveyTemplatesPage from "@/pages/pulse-survey-templates";
import ProtectedRoute from "@/components/protected-route";
import { useEffect } from "react";

import NotFound from "@/pages/not-found";

// Route the root path to the correct landing page based on role
function HomeRedirect() {
  const { userPermissions } = useAuth();
  if (userPermissions.includes('sales')) {
    return <Redirect to="/sales-dashboard" />;
  }
  return <Redirect to="/dashboard" />;
}

// Component to capture intended path for unauthenticated users
function CaptureAndRedirect({ to }: { to: string }) {
  const [location, setLocation] = useLocation();
  
  useEffect(() => {
    // Store the current full path if it's not the login page
    if (location !== '/login') {
      const fullPath = `${location}${window.location.search}${window.location.hash}`;
      sessionStorage.setItem('intendedPath', fullPath);
    }
    setLocation(to);
  }, [location, to, setLocation]);
  
  return null;
}



function Router() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Login page without sidebar
  if (!user) {
    return (
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/verify-otp" component={VerifyOtp} />
        <Route path="/" component={() => <CaptureAndRedirect to="/login" />} />
        <Route component={() => <CaptureAndRedirect to="/login" />} />
      </Switch>
    );
  }

  // Protected routes with sidebar layout
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      <Switch>
        <Route path="/articles/create">
          <ProtectedRoute requiredPage="articles">
            <CreateArticle />
          </ProtectedRoute>
        </Route>
        <Route path="/articles">
          <ProtectedRoute requiredPage="articles">
            <Articles />
          </ProtectedRoute>
        </Route>

        <Route path="/videos/edit">
          <ProtectedRoute requiredPage="videos">
            <EditVideo />
          </ProtectedRoute>
        </Route>
        <Route path="/series">
          <ProtectedRoute requiredPage="series">
            <SeriesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/categories">
          <ProtectedRoute requiredPage="categories">
            <CategoriesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/contributors">
          <ProtectedRoute requiredPage="authors">
            <AuthorsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/badges/order">
          <ProtectedRoute requiredPage="badges">
            <BadgeOrderPage />
          </ProtectedRoute>
        </Route>
        <Route path="/badges">
          <ProtectedRoute requiredPage="badges">
            <BadgesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/milestones">
          <ProtectedRoute requiredPage="milestones">
            <MilestonesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/promotions">
          <ProtectedRoute requiredPage="promotions">
            <PromotionsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/events">
          <ProtectedRoute requiredPage="events">
            <EventsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/events/details">
          <ProtectedRoute requiredPage="events">
            <EventDetailsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/programs/edit/:id">
          <ProtectedRoute requiredPage="programs">
            <AddProgramPage />
          </ProtectedRoute>
        </Route>
        <Route path="/programs/add">
          <ProtectedRoute requiredPage="programs">
            <AddProgramPage />
          </ProtectedRoute>
        </Route>
        <Route path="/programs">
          <ProtectedRoute requiredPage="programs">
            <ProgramsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/partnerships">
          <ProtectedRoute requiredPage="partnerships">
            <PartnershipsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/newsletters/template">
          <ProtectedRoute requiredPage="newsletters">
            <NewsletterTemplatePage />
          </ProtectedRoute>
        </Route>
        <Route path="/newsletters/edit/:id">
          <ProtectedRoute requiredPage="newsletters">
            <AddNewsletterPage />
          </ProtectedRoute>
        </Route>
        <Route path="/newsletters/add">
          <ProtectedRoute requiredPage="newsletters">
            <AddNewsletterPage />
          </ProtectedRoute>
        </Route>
        <Route path="/newsletters">
          <ProtectedRoute requiredPage="newsletters">
            <NewslettersPage />
          </ProtectedRoute>
        </Route>
        <Route path="/customers">
          <ProtectedRoute requiredPage="customers">
            <CustomersPage />
          </ProtectedRoute>
        </Route>
        <Route path="/trials">
          <ProtectedRoute requiredPage="trials">
            <TrialsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/sales-dashboard">
          <ProtectedRoute requiredPage="sales-dashboard">
            <SalesDashboard />
          </ProtectedRoute>
        </Route>
        <Route path="/company-audit">
          <ProtectedRoute requiredPage="company-audit">
            <CompanyAuditPage />
          </ProtectedRoute>
        </Route>
        <Route path="/user-management">
          <ProtectedRoute requiredPage="user-management">
            <UserManagement />
          </ProtectedRoute>
        </Route>
        <Route path="/settings">
          <ProtectedRoute requiredPage="settings">
            <Settings />
          </ProtectedRoute>
        </Route>
        <Route path="/import">
          <ProtectedRoute requiredPage="categories">
            <ImportPage />
          </ProtectedRoute>
        </Route>
        <Route path="/pulse-surveys/categories">
          <ProtectedRoute requiredPage="pulse-survey-categories">
            <PulseSurveyCategoriesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/pulse-surveys/questions">
          <ProtectedRoute requiredPage="pulse-survey-questions">
            <PulseSurveyQuestionsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/pulse-surveys/templates">
          <ProtectedRoute requiredPage="pulse-survey-templates">
            <PulseSurveyTemplatesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/login" component={() => <Redirect to="/" />} />
        <Route path="/verify-otp" component={() => <Redirect to="/" />} />
        <Route path="/dashboard">
          <ProtectedRoute requiredPage="videos">
            <Dashboard />
          </ProtectedRoute>
        </Route>
        <Route path="/" component={HomeRedirect} />
        <Route component={NotFound} />
      </Switch>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Router />
          <Toaster />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
